import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, AlertCircle, Plus, Calendar, X, Sidebar } from 'lucide-react';
import type { Todo, Month, TaskStatus, TodoCreate } from '../types';
import { monthsAPI } from '../services/api';
import TodoItem from './TodoItem';
import ReviewModal from './ReviewModal';
import MonthSummary from './MonthSummary';
import { SearchFilterBar } from './SearchFilterBar';
import { useNotification } from '../hooks/useNotification';

interface MonthViewProps {
  selectedTeamId?: string | null;
  onTodoClick: (todo: Todo, modalContext?: {
    previousModal?: string;
    previousPhase?: string;
    previousState?: Record<string, unknown>;
  }) => void;
  onTodoStatusChange: (todoId: string, status: TaskStatus) => void;
  onAddTodo: () => void;
  onAddChild: (parentId: string) => void;
  onMakeChild: (childId: string, parentId: string) => void;
  onStartPomodoro?: (todo: Todo) => void;
  onToggleActiveWeek?: (todoId: string, isActive: boolean) => void;
  onDelete?: (todoId: string) => void;
  teamMembers: Array<{ id: string; name: string; email: string }>;
  isPersonalMode: boolean;
  allTodos: Todo[];
  onCreateTodo?: (todo: TodoCreate) => Promise<Todo>;
  onUpdateTodo?: (todoId: string, updates: Partial<Todo>) => Promise<void>;
}

const MonthView: React.FC<MonthViewProps> = ({
  selectedTeamId,
  onTodoClick,
  onTodoStatusChange,
  onAddTodo,
  onAddChild,
  onMakeChild,
  onStartPomodoro,
  onToggleActiveWeek,
  onDelete,
  teamMembers,
  isPersonalMode,
  allTodos,
  onUpdateTodo
}) => {
  const [currentMonth, setCurrentMonth] = useState<Month | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActiveTasksExpanded, setIsActiveTasksExpanded] = useState(true);
  const [isDraftTasksExpanded, setIsDraftTasksExpanded] = useState(true);
  const [isDraggingOverMonth, setIsDraggingOverMonth] = useState(false);
  const [showMonthlyReview, setShowMonthlyReview] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [hoveredAddButton, setHoveredAddButton] = useState<string | null>(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [horizonFilter, setHorizonFilter] = useState<string[]>([]);
  const [tagsFilter, setTagsFilter] = useState<string[]>([]);
  const [taskTypeFilter, setTaskTypeFilter] = useState<'all' | 'parent_only' | 'child_only'>('all');
  
  const { showNotification } = useNotification();

  // Check for pending review from widget navigation
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingReview');
    if (pending === 'monthly') {
      sessionStorage.removeItem('pendingReview');
      setShowMonthlyReview(true);
    }
  }, []);

  // Listen for reopen monthly review events
  useEffect(() => {
    const handleReopenMonthlyReview = () => {
      setShowMonthlyReview(true);
    };
    window.addEventListener('reopenMonthlyReview', handleReopenMonthlyReview as EventListener);
    return () => {
      window.removeEventListener('reopenMonthlyReview', handleReopenMonthlyReview as EventListener);
    };
  }, []);

  // Previous month data for review context
  const [previousMonthData, setPreviousMonthData] = useState<{
    goals?: string[];
    reflections?: import('../types').ReviewEntry[];
    completedTaskCount?: number;
  } | null>(null);
  const [isLoadingPreviousMonth, setIsLoadingPreviousMonth] = useState(false);

  const loadPreviousMonthData = useCallback(async () => {
    if (!currentMonth) return;
    try {
      setIsLoadingPreviousMonth(true);
      const currentMonthDate = new Date(currentMonth.start_date);
      const previousMonthDate = new Date(currentMonthDate);
      previousMonthDate.setMonth(previousMonthDate.getMonth() - 1);

      const months = await monthsAPI.getAll();
      const prevMonth = months.find((m: Month) => {
        const monthStart = new Date(m.start_date);
        return monthStart.getFullYear() === previousMonthDate.getFullYear() &&
               monthStart.getMonth() === previousMonthDate.getMonth();
      });

      if (prevMonth) {
        const tasksResponse = await monthsAPI.getTasks(prevMonth.id) as { completed_tasks?: Todo[] };
        setPreviousMonthData({
          goals: prevMonth.goals,
          reflections: prevMonth.reflections,
          completedTaskCount: tasksResponse.completed_tasks?.length ?? 0,
        });
      } else {
        setPreviousMonthData(null);
      }
    } catch (error) {
      console.error('Failed to load previous month data:', error);
      setPreviousMonthData(null);
    } finally {
      setIsLoadingPreviousMonth(false);
    }
  }, [currentMonth]);

  // Load previous month data when review opens
  useEffect(() => {
    if (showMonthlyReview && currentMonth) {
      loadPreviousMonthData();
    }
  }, [showMonthlyReview, currentMonth, loadPreviousMonthData]);

  // Filter handlers
  const onStatusToggle = (status: TaskStatus) => {
    setStatusFilter(prev => 
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const onHorizonToggle = (horizon: string) => {
    setHorizonFilter(prev =>
      prev.includes(horizon) ? prev.filter(h => h !== horizon) : [...prev, horizon]
    );
  };

  const onTagToggle = (tag: string) => {
    setTagsFilter(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const onClearFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setHorizonFilter([]);
    setTagsFilter([]);
    setTaskTypeFilter('all');
  };

  // Apply filters to allTodos
  const filteredTodos = allTodos.filter(todo => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const searchableText = `${todo.title} ${todo.description || ''} ${todo.tags?.join(' ') || ''}`.toLowerCase();
      if (!searchableText.includes(query)) return false;
    }

    // Status filter
    if (statusFilter.length > 0 && !statusFilter.includes(todo.status)) {
      return false;
    }

    // Horizon filter
    if (horizonFilter.length > 0 && !horizonFilter.includes(todo.horizon || '')) {
      return false;
    }

    // Tags filter
    if (tagsFilter.length > 0 && (!todo.tags || !todo.tags.some(tag => tagsFilter.includes(tag)))) {
      return false;
    }

    // Task type filter
    if (taskTypeFilter === 'parent_only' && todo.parent_id) {
      return false;
    }
    if (taskTypeFilter === 'child_only' && !todo.parent_id) {
      return false;
    }

    return true;
  });

  // Derive tasks from filtered todos
  // Month view shows tasks with horizon 'soon' or 'later', plus any with deadlines in this month
  const activeTasks = filteredTodos.filter(todo => {
    // Show tasks with horizon 'soon' or 'later'
    if (todo.horizon === 'soon' || todo.horizon === 'later') return true;

    // Also show tasks with a deadline within this month
    if (currentMonth && todo.deadline) {
      const monthEndStr = currentMonth.end_date.split('T')[0];
      const deadlineStr = todo.deadline.split('T')[0];
      if (deadlineStr <= monthEndStr) return true;
    }

    return false;
  });

  const draftTasks: Todo[] = [];

  // Get all unique tags from allTodos for filter
  const allTags = Array.from(new Set(
    allTodos.flatMap(todo => todo.tags || [])
  )).sort();

  const loadCurrentMonth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get current month - we only need the month metadata, not the tasks
      const month = await monthsAPI.getCurrent(selectedTeamId || undefined);
      setCurrentMonth(month);
      
    } catch (err) {
      setError('Failed to load month data');
      console.error('Error loading month:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTeamId]);

  useEffect(() => {
    loadCurrentMonth();
  }, [selectedTeamId, loadCurrentMonth]);

  const formatMonthRange = (month: Month) => {
    const date = new Date(month.start_date);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };


  const handleUpdateMonth = async (updates: Partial<Month>) => {
    if (!currentMonth) return;
    
    try {
      await monthsAPI.update(currentMonth.id, updates);
      // Update local month state
      setCurrentMonth(prev => prev ? { ...prev, ...updates } : null);
      showNotification('success', 'Month updated successfully!');
    } catch (error) {
      console.error('Failed to update month:', error);
      showNotification('error', 'Failed to update month. Please try again.');
    }
  };

  const handleMarkReviewComplete = async () => {
    if (!currentMonth) return;
    
    try {
      await monthsAPI.completeReview(currentMonth.id);
      // Update local month state
      setCurrentMonth(prev => prev ? { ...prev, is_review_completed: true } : null);
      showNotification('success', 'Monthly review completed successfully!');
    } catch (error) {
      console.error('Failed to complete review:', error);
      showNotification('error', 'Failed to complete review. Please try again.');
    }
  };

  const handleDragToMonth = async (todoId: string) => {
    try {
      const todo = allTodos.find(t => t.id === todoId);
      if (!todo) return;

      if (todo.horizon !== 'soon' && todo.horizon !== 'later') {
        await onUpdateTodo?.(todoId, { horizon: 'soon' });
        showNotification('success', 'Task scheduled for this month!');
      } else {
        showNotification('info', 'Task already scheduled within this month');
      }
    } catch (error) {
      console.error('Failed to add task to month:', error);
      showNotification('error', 'Failed to add task. Please try again.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverMonth(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverMonth(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverMonth(false);
    
    const todoId = e.dataTransfer.getData('text/plain');
    if (todoId) {
      await handleDragToMonth(todoId);
    }
  };


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!currentMonth) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No month data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Month Header - Always visible */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div className="flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-blue-600" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {formatMonthRange(currentMonth)}
                </h1>
              </div>
              
              <div className="flex items-center space-x-3 sm:space-x-6 flex-1">
                {/* Search and Filter Bar */}
                <div className="flex-1 sm:max-w-xl">
                  <SearchFilterBar
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                    statusFilter={statusFilter}
                    onStatusToggle={onStatusToggle}
                    horizonFilter={horizonFilter}
                    onHorizonToggle={onHorizonToggle}
                    tagsFilter={tagsFilter}
                    onTagToggle={onTagToggle}
                    taskTypeFilter={taskTypeFilter}
                    onTaskTypeChange={setTaskTypeFilter}
                    allTags={allTags}
                    onClearFilters={onClearFilters}
                  />
                </div>
                
                {/* Sidebar Toggle */}
                <button
                  type="button"
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="hidden sm:flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={showSidebar ? 'Hide month summary' : 'Show month summary'}
                >
                  <Sidebar className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden xl:inline">
                    {showSidebar ? 'Hide Summary' : 'Month Summary'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
        {/* Main Content (Tasks) */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="max-w-7xl mx-auto space-y-4 px-4 sm:px-6 lg:px-8 py-4">

            <div className="space-y-4">
              {/* Active This Month Section */}
              <div 
                className={`
                  bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200
                  ${isDraggingOverMonth ? 'bg-blue-50 border-blue-400 shadow-lg' : ''}
                `}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
                  <button
                    type="button"
                    onClick={() => setIsActiveTasksExpanded(!isActiveTasksExpanded)}
                    className="flex-1 text-left"
                    title="Tasks scheduled for this month"
                  >
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                      {isActiveTasksExpanded ? (
                        <ChevronDown className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                      )}
                      <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                      Active This Month
                      <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                        ({activeTasks.length})
                      </span>
                      {isDraggingOverMonth && (
                        <span className="ml-2 text-sm font-medium text-blue-600 animate-pulse">
                          Drop here to schedule for this month
                        </span>
                      )}
                    </h2>
                  </button>
                  
                  <button
                    type="button"
                    onClick={onAddTodo}
                    onMouseEnter={() => setHoveredAddButton('active')}
                    onMouseLeave={() => setHoveredAddButton(null)}
                    className="ml-3 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                    title="Add new task"
                  >
                    {hoveredAddButton === 'active' ? (
                      <X className="w-4 h-4 transform rotate-45 transition-transform duration-200" />
                    ) : (
                      <Plus className="w-4 h-4 transition-transform duration-200" />
                    )}
                  </button>
                </div>
                
                {isActiveTasksExpanded && (
                  <div className="px-3 pb-3">
                    <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>
                    {activeTasks.length === 0 ? (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">No active tasks for this month</p>
                        <button type="button" onClick={onAddTodo} className="btn-primary">
                          Add Task
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {activeTasks.map((todo) => (
                          <TodoItem
                            key={todo.id}
                            todo={todo}
                            onClick={() => onTodoClick(todo)}
                            onStatusChange={(status) => onTodoStatusChange(todo.id, status)}
                            onSubtaskStatusChange={onTodoStatusChange}
                            onAddChild={onAddChild}
                            onSubtaskClick={onTodoClick}
                            onMakeChild={onMakeChild}
                            onStartPomodoro={onStartPomodoro}
                            onToggleActiveWeek={onToggleActiveWeek}
                            onDelete={onDelete}
                            allTodos={allTodos}
                            teamMembers={teamMembers}
                            isPersonalMode={isPersonalMode}
                            isDraggable={true}
                            hideActiveWeekIndicator={false}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Draft Tasks Section */}
              {draftTasks.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg">
                    <button
                      type="button"
                      onClick={() => setIsDraftTasksExpanded(!isDraftTasksExpanded)}
                      className="flex-1 text-left"
                    >
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                        {isDraftTasksExpanded ? (
                          <ChevronDown className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
                        )}
                        <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
                        Draft Tasks
                        <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                          ({draftTasks.length})
                        </span>
                      </h2>
                    </button>
                  </div>
                  
                  {isDraftTasksExpanded && (
                    <div className="px-3 pb-3">
                      <div className="border-b border-gray-200 dark:border-gray-700 mb-4"></div>
                      <div className="space-y-2">
                        {draftTasks.map((todo) => (
                          <TodoItem
                            key={todo.id}
                            todo={todo}
                            onClick={() => onTodoClick(todo)}
                            onStatusChange={(status) => onTodoStatusChange(todo.id, status)}
                            onSubtaskStatusChange={onTodoStatusChange}
                            onAddChild={onAddChild}
                            onSubtaskClick={onTodoClick}
                            onMakeChild={onMakeChild}
                            onStartPomodoro={onStartPomodoro}
                            onToggleActiveWeek={onToggleActiveWeek}
                            onDelete={onDelete}
                            allTodos={allTodos}
                            teamMembers={teamMembers}
                            isPersonalMode={isPersonalMode}
                            isDraggable={true}
                            hideActiveWeekIndicator={false}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar (Summary) - Desktop only */}
        {showSidebar && (
          <div className="hidden lg:block w-80 xl:w-96 border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
            <MonthSummary
              month={currentMonth}
              activeTasks={activeTasks}
              draftTasks={draftTasks}
              onStartMonthlyReview={() => setShowMonthlyReview(true)}
            />
          </div>
        )}
      </div>

      {/* Monthly Review Modal */}
      {currentMonth && (
        <ReviewModal
          isOpen={showMonthlyReview}
          onClose={() => setShowMonthlyReview(false)}
          reviewType="monthly"
          periodLabel={new Date(currentMonth.start_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          reflections={currentMonth.reflections}
          goals={currentMonth.goals}
          previousPeriod={previousMonthData}
          isLoadingPreviousData={isLoadingPreviousMonth}
          onSave={(data) => handleUpdateMonth({ ...data, is_review_completed: false })}
          onComplete={(data) => {
            handleUpdateMonth({ ...data, is_review_completed: true });
            handleMarkReviewComplete();
          }}
        />
      )}
    </div>
  );
};

export default MonthView;